import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardMedia,
  Typography,
  CircularProgress,
  Box,
  Button,
} from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import Tab from '@mui/material/Tab';
import TabPanel from '@mui/lab/TabPanel';
import TabList from '@mui/lab/TabList';
import { styled } from '@mui/system';
import { Footer } from '../0_layout/Footer';
import { Header } from '../0_layout/HeaderFooter';
import { useNavigate } from 'react-router-dom';

// Styled components

const LightGreenSpinner = styled(CircularProgress)({
  color: '#66bb6a',
  filter: 'drop-shadow(0 0 6px #66bb6a)',
});


const BlogDisplay = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [value, setValue] = useState('2');
  //const [selectedType, setSelectedType] = useState('blog');
  // const [selectedArticle, setSelectedArticle] = useState(null);
  const [news, setNews] = useState([]);


  const handleChange = (event, newValue) => {
    setValue(newValue);
  };


  //const theme = useTheme(); // ✅ Fix: Get MUI theme
  //const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // ✅ Use theme properly

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        // Fetch blogs
        const blogRes = await fetch(`${process.env.REACT_APP_API_URL}/api/blogs`);
        const blogData = await blogRes.json();

        const publishedBlogs = blogData
          .filter(item => item.status === 'published' && item.type === 'blog')
          .reverse();

        setBlogs(publishedBlogs);

        // Fetch news
        const newsRes = await fetch(`${process.env.REACT_APP_API_URL}/api/news`);
        const newsData = await newsRes.json();  // ✅ NO FILTER here

        setNews(newsData.reverse());  // ✅ Use full response without filtering
      } catch (err) {
        console.error('❌ Error fetching articles:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);





  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Date not available';

    const publishedDate = new Date(dateString);
    if (isNaN(publishedDate)) return 'Invalid date';

    const now = new Date();
    const diffTime = now - publishedDate;


    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffMinutes < 60) return 'Updated just now';
    if (diffHours < 24) return diffHours === 1 ? 'Updated 1 hour ago' : `Updated ${diffHours} hours ago`;
    if (diffDays === 1) return 'Updated 1 day ago';
    if (diffDays <= 15) return `Updated ${diffDays} days ago`;

    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks <= 8) return diffWeeks === 1 ? 'Updated 1 week ago' : `Updated ${diffWeeks} weeks ago`;

    const diffMonths = Math.floor(diffDays / 30); // Approximate month
    return diffMonths === 1 ? 'Updated 1 month ago' : `Updated ${diffMonths} months ago`;
  };


  const renderArticleCard = (article) => (
    <Grid item xs={12} key={article._id}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Card
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
            borderRadius: '16px',
            overflow: 'hidden',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease', // Add transition
            '&:hover': {
              transform: 'scale(1.02)', // Slightly zoom in
              boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.2)', // Stronger shadow
            },
          }}
        >
          {/* Image Section */}
          <CardMedia
            component="img"
            image={article.imageURL || 'default.jpg'}
            alt={article.title}
            sx={{
              width: { xs: '100%', sm: '40%' },
              height: '100%',
              objectFit: 'cover',
              display: 'block',       // <-- removes inline spacing
              margin: 0,              // <-- ensures no extra margin
              padding: 0,             // <-- ensures no padding
              border: 0,              // <-- removes any border
              lineHeight: 0           // <-- prevents image from reserving line height
            }}
          />


          {/* Content Section */}
          <Box sx={{ px: 3, pb: 3, pt: 0, width: { sm: '60%' } }}>
            <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
              {article.title}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              By {article.author}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {(article.shortDescription || '').substring(0, 250)}...
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() =>
                navigate(`/${article.type}/${article._id}`, {
                  state: { article },
                })
              }
              sx={{
                backgroundColor: '#1976d2',
                color: '#fff',
                fontWeight: 'bold',
                fontSize: '14px',
                padding: '10px 0',
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              READ MORE
            </Button>
          </Box>
        </Card>
      </Box>
    </Grid>
  );

  if (loading) return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="300px"
    >
      <LightGreenSpinner size={50} thickness={5} />
      <Typography variant="h6" mt={2}>
        Loading...
      </Typography>
    </Box>
  );


  const renderNewsCard = (news) => (
    <Grid item xs={12} key={news._id}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Card
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
            borderRadius: '16px',
            overflow: 'hidden',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease', // Add transition
            '&:hover': {
              transform: 'scale(1.02)', // Slightly zoom in
              boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.2)', // Stronger shadow
            },
          }}
        >
          {/* Image Section */}
          <Box
            sx={{
              width: { xs: '100%', sm: '40%' },
              position: 'relative',
              borderRadius: '16px',
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={news.imageURL || 'default.jpg'}
              alt={news.title}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />

            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '8px',
                fontSize: '12px',
              }}
            >
              {getTimeAgo(news.date || news.createdAt)}

            </Typography>
          </Box>


          {/* Content Section */}
          <Box sx={{ px: 3, pb: 3, pt: 0, width: { sm: '60%' } }}>
            <Typography variant="h5" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
              {news.title}
            </Typography>
            {/*<Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Field: {news.field}
            </Typography>*/}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {(news.shortDescription || '').substring(0, 250)}...
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() =>
                navigate(`/news/${news._id}`, {
                  state: { article: news }
                })

              }
              sx={{
                backgroundColor: '#1976d2',
                color: '#fff',
                fontWeight: 'bold',
                fontSize: '14px',
                padding: '10px 0',
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              READ MORE
            </Button>
          </Box>
        </Card>
      </Box>
    </Grid>
  );

  if (loading) return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="300px"
    >
      <LightGreenSpinner size={50} thickness={5} />
      <Typography variant="h6" mt={2}>
        Loading...
      </Typography>
    </Box>
  );



  return (
    <>
      <Header />
      <Container maxWidth="xl" sx={{ mt: { lg: 17, md: 20, sm: 14, xs: 27 }, mb: 4 }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontWeight: 'bold', color: '#333', fontSize: { sm: '35px', xs: '30px' } }}
        >
          Articles
        </Typography>


        <Box sx={{ width: '100%', typography: 'body1', textAlign: 'center', mt: 4 }}>
          <TabContext value={value}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList onChange={handleChange} aria-label="News/Blog Tabs">
                <Tab label="BLOG" value="2" />
                <Tab label="NEWS" value="1" />
              </TabList>
            </Box>

            <TabPanel value="1">
              <Box sx={{ maxWidth: '900px', margin: '0 auto' }}>
                <Grid container spacing={4} justifyContent="center">
                  {news.map((item, index) => (
                    <React.Fragment key={item._id || index}>
                      {renderNewsCard(item)}
                    </React.Fragment>
                  ))}

                  {news.length === 0 && (
                    <Typography textAlign="center" sx={{ mt: 4 }}>
                      No news available.
                    </Typography>
                  )}


                </Grid>
              </Box>
            </TabPanel>

            <TabPanel value="2">
              <Box sx={{ maxWidth: '900px', margin: '0 auto' }}>
                <Grid container spacing={4} justifyContent="center">
                  {blogs.map((blog) => (
                    <React.Fragment key={blog._id}>
                      {renderArticleCard(blog)}
                    </React.Fragment>
                  ))}

                </Grid>
              </Box>
            </TabPanel>

          </TabContext>
        </Box>


      </Container>
      <Footer />
    </>
  );
};

export default BlogDisplay;
